import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { subscriptionActions } from '../../constants';
import { onSwitchPageLoadECD } from '../../helpers/switch/analytics';
import { isFreeTrial, getFreeTrialData } from '../../helpers/subscription';
import eligibleForCeTrial from '../../actions/ecd/resolvers/eligibleForCeTrial';

/**
 * Custom hook to handle ECD page load tracking for payment and product movement intent
 * @param {Object} targetOffer - The target offer being switched to
 * @param {Array} familiesNotRequiringPayment - Array of product families that don't require payment
 * @param {boolean} isFlexFlow - Whether the current journey is the explicit new flex flow
 */
const usePaymentPageLoadAnalytics = (targetOffer, familiesNotRequiringPayment = [], isFlexFlow = false) => {
  const subscription = useSelector(state => state.subscriptions.current);
  const billing = useSelector(state => state.billing);
  const offers = useSelector(state => state.offers);
  useEffect(() => {
    if (isFlexFlow) {
      return;
    }

    if (targetOffer && subscription && billing && !familiesNotRequiringPayment.includes(targetOffer.family)) {
      const freeTrialData = getFreeTrialData(billing?.freeTrialTerms, subscription.freeTrialEndDt);
      const isInFreeTrial = freeTrialData.ends !== undefined;
      const isPendingCancellation = subscription.defermentInfo?.type === subscriptionActions.cancellation;
      const isEligibleForTrial =
        isFreeTrial(targetOffer.descriptors) ||
        eligibleForCeTrial({
          offers,
          subscriptions: { current: subscription },
        });

      onSwitchPageLoadECD({
        currentSubscriptionFamily: subscription.family || 'unknown',
        targetSubscriptionFamily: targetOffer.family || 'unknown',
        userEligibleForTrial: isEligibleForTrial ?? 'not_set',
        userCurrentlyInFreeTrial: isInFreeTrial,
        subscriptionPendingCancellation: isPendingCancellation,
        remainingTrialDays: freeTrialData.remaining,
      });
    }
  }, [targetOffer, familiesNotRequiringPayment, subscription, billing, offers, isFlexFlow]);
};

export default usePaymentPageLoadAnalytics;
